import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';
import { RevGpMapping } from 'src/models/xPTMappingModels';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';

/**
 * Service for validating (Channel, Product Line) combinations against the rev_gp mapping
 * sourced from Cognos (COGNOS_REVGP_HIERARCHY).
 */
export class RevGpValidationService extends ValidationService {
  /**
   * Validates that each row's (Channel, Product Line) pair exists in the rev_gp mapping.
   *
   * BG onboarding can mark either Channel or Product Line as `corp_segment_required=false`, in which
   * case that column is not shown in the forecast grid and every row inherits the BG's
   * `corp_segment_default_value.segment_hierarchy[0]` for that segment at submit time
   * (see `prepareForecastTemplateUploadData` in ForecastTemplateUtils.ts). The caller passes those
   * BG defaults here so validation at grid time matches what the payload will actually carry.
   *
   * If BOTH defaults are provided, both columns are grid-hidden and every row would resolve to the
   * exact same (defaultChannel, defaultProductLine) pair. That pair is guarded by the BG Onboarding
   * form itself, so this per-row check is a no-op — return success early.
   *
   * Scope: only rows with `is_new = true` are checked. Baseline rows (`!is_new`) are skipped
   * because both Channel and Product Line are locked on them (they're mandatory corp segments,
   * and NonEditableFieldsValidationService catches any admin who edits either), so surfacing a
   * rev_gp mismatch on a baseline row is a dead-end — either the master data drifted since the
   * row was originally submitted (nothing the user can fix), or the user's edit will already be
   * flagged as a non-editable-field violation elsewhere in the pipeline.
   *
   * @param forecastTemplateRelevantRowData Rows to validate (dirty rows on grid save; transformed modified rows on upload).
   * @param revGpMapping Full list of valid (channel, product_line) combinations from S3.
   * @param defaultChannel Optional fallback Channel for BGs where Channel is not required.
   * @param defaultProductLine Optional fallback Product Line for BGs where Product Line is not required.
   * @returns Promise with validation status
   */
  public async validateChannelProductLineCombination(
    forecastTemplateRelevantRowData: any[],
    revGpMapping: RevGpMapping[],
    defaultChannel?: string,
    defaultProductLine?: string
  ): Promise<ValidationStatusEntity> {
    try {
      const validationErrorDetails: ValidationErrorDetail[] = [];
      const trimmedDefaultChannel = typeof defaultChannel === 'string' ? defaultChannel.trim() : undefined;
      const trimmedDefaultProductLine = typeof defaultProductLine === 'string' ? defaultProductLine.trim() : undefined;

      // When both segments are grid-hidden (BG-defaulted), every row carries the same fixed pair.
      // The BG onboarding form validates that pair once; nothing per-row to check.
      if (trimmedDefaultChannel && trimmedDefaultProductLine) {
        logger.info('Skipping rev_gp validation — both Channel and Product Line are BG-defaulted; onboarding form guards the pair.');
        return this.createSuccessStatus(
          ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_SUCCESS,
          ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_DEFAULT
        );
      }

      // Skip validation when the mapping is unavailable (S3 fetch pending or failed). Without this guard
      // an empty mapping would falsely flag every row as an invalid combination and block save entirely.
      // Fail open with a distinct message so users understand what happened.
      if (!revGpMapping || revGpMapping.length === 0) {
        logger.warn('rev_gp mapping is empty; skipping Channel & Product Line combination validation (S3 fetch pending or failed).');
        return this.createSuccessStatus(
          ForecastValidationMessages.REV_GP_MAPPING_UNAVAILABLE,
          ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_DEFAULT
        );
      }

      logger.info(`Validating Channel & Product Line combination for ${forecastTemplateRelevantRowData.length} records`);

      // O(1) membership lookup by concatenated key. Trim to keep the Set-key side symmetric with row
      // values (also trimmed above) — guards against any whitespace in the S3 payload leaking through
      // if the slice's ingest-time normalization is ever bypassed.
      const validPairs = new Set<string>(
        revGpMapping.map((entry) => `${entry.channel.trim()}|${entry.product_line.trim()}`)
      );

      forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
        // Baseline rows (`!is_new`) are out of scope — Channel and Product Line are locked on
        // them, so a rev_gp mismatch either reflects master-data drift the user can't fix or a
        // locked-field edit that another validator will catch. See method doc.
        if (!row[ForecastGridFixedFields.IsNew.value]) {
          return;
        }

        const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
        const rawChannel = row[CorpSegmentNames.CHANNEL];
        const rawProductLine = row[CorpSegmentNames.PRODUCT_LINE];
        const rowChannel = typeof rawChannel === 'string' ? rawChannel.trim() : rawChannel;
        const rowProductLine = typeof rawProductLine === 'string' ? rawProductLine.trim() : rawProductLine;

        // Fall back to BG defaults when the row value is empty. Either field may be BG-defaulted
        // (non-required segment). If neither the row nor the default provides a value, skip that field —
        // mandatory-field validation will flag the missing required-segment for BGs that expect one.
        const channel = rowChannel || trimmedDefaultChannel;
        const productLine = rowProductLine || trimmedDefaultProductLine;
        if (!channel || !productLine) {
          return;
        }

        if (!validPairs.has(`${channel}|${productLine}`)) {
          validationErrorDetails.push({
            rowIndex: rowNo,
            message: `Channel ${channel} and Product Line ${productLine} is not a valid combination ${this.getLineItemIdMessagePart(
              row,
              ForecastGridFixedFields.XptLineItemId.value
            )}`
          });
        }
      });

      this.logValidationErrors('Channel & Product Line Mapping', validationErrorDetails);

      if (validationErrorDetails.length > 0) {
        return this.createErrorStatus(
          ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_FAILED,
          ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_DEFAULT,
          validationErrorDetails
        );
      }

      return this.createSuccessStatus(
        ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_SUCCESS,
        ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_DEFAULT
      );
    } catch (error: any) {
      logger.error('Error validating Channel & Product Line Mapping:', error);
      return this.createErrorStatus(
        ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_ERROR,
        ForecastValidationMessages.REV_GP_MAPPING_VALIDATION_DEFAULT,
        [{ message: error.message || 'Unknown error validating Channel and Product Line combination' }]
      );
    }
  }
}

// Export singleton instance
export const revGpValidationService = new RevGpValidationService();
